import {useEffect, useMemo, useRef, useState} from 'react';
import {format, parseISO} from 'date-fns';
import {useTranslation} from 'react-i18next';
import {Navigate, useParams} from 'react-router-dom';

import {getAppLanguage, UICalendarWeek, useAppSelector, useOverridableFeatureFlags} from '@lib/store';
import {Loader} from '@atoms';
import {useModal} from '@hooks';
import {trackPageComponentedRendered} from '@lib/infrastructure';
import {
  constructCalendarOverviewConnectionFilter,
  constructCalendarWeeks,
  isBeforeToday,
  useFilterData,
} from '@lib/logic';
import {formatDate} from '@lib/utils';
import {IconButton} from '@molecules';
import {
  CalendarDayOverview,
  CalendarHorizontalWeekOverview,
  CalendarOverviewConnectionFilter,
  CalendarWeek,
  CalendarWeekDay,
  NewFilterCard,
} from '@organisms';
import {FlexRow, H1, H4} from '@quarks';
import {
  CalendarLayout,
  CalendarWeekOverview,
  Canvas,
  Heading,
  CalendarDayOverview as StyledCalendarDayOverview,
} from '@templates';
import {CalendarHorizontalWeekOverviewWrapper, CalendarPageHeader, OverviewWrapper} from './styles';
import {
  getConnectedUsersWithAndWithoutBookingsV2,
  getIsCalendarDataLoading,
} from '@lib/store/src/data/timeslots/selectors';
import {DaySummary} from 'src/components/organisms/calendar/DaySummary/DaySummary';
import {WorkdayActions} from 'src/components/organisms/calendar/WorkdayTile/WorkdayActions';
import {cn} from '@utils';

let hasScrolledToPosition = false;

export const Calendar = () => {
  const appLanguage = useAppSelector(getAppLanguage);

  const features = useOverridableFeatureFlags();

  const {addPages, openModal} = useModal();

  const [showCalendar, setShowCalendar] = useState(true);

  // Note: for long sessions we should update this if we don't want to rely on there being a re-render
  //       every now and then
  const todayKey = format(new Date(), 'yyyy-MM-dd');
  const calendarWeeks = useMemo<UICalendarWeek[]>(() => constructCalendarWeeks(todayKey), [todayKey]);

  const isCalendarDataLoading = useAppSelector(getIsCalendarDataLoading(calendarWeeks));

  const {t} = useTranslation();

  const toggleDayAndWeekView = () => {
    window.scrollTo(0, 0);
    setShowCalendar((prev) => !prev);
  };

  const {connectionsWithWorkdays, connectionsWithoutWorkdays} = useAppSelector((state) => {
    return getConnectedUsersWithAndWithoutBookingsV2(state);
  });
  const filters = useMemo(
    () => constructCalendarOverviewConnectionFilter(connectionsWithWorkdays, connectionsWithoutWorkdays, t),
    [connectionsWithWorkdays, connectionsWithoutWorkdays, t],
  );
  const {filteredData} = useFilterData(
    'CalendarOverview_Connections',
    'cacheKey',
    [...connectionsWithWorkdays, ...connectionsWithoutWorkdays],
    filters,
    'or',
    isCalendarDataLoading,
  );
  const selectedCalendarConnectionFilterIds = filteredData.map((c) => c.id);

  useEffect(() => {
    trackPageComponentedRendered('calendar', !isCalendarDataLoading);
  }, [isCalendarDataLoading]);

  // Get the active date from the path
  const {date: selectedDateKey} = useParams();

  // Redirect all invalid things from the path to today (note: no 404)
  const selectedDateIsInRange = calendarWeeks.some((w) => w.data.some((d) => d.date === selectedDateKey));
  const redirectToToday = !selectedDateKey || !selectedDateIsInRange;

  useEffect(
    function scrollToSelectedDate() {
      if (redirectToToday || hasScrolledToPosition) return;

      // TODO: this should be triggered by the actual day tile that renders with the isToday state.
      //       the current timeout will not be a reliable guarantee that the element exists and is
      //       ready to be scrolled to. Also, it targets body rather than its own subtree...
      let to = setTimeout(() => {
        const scrollElement = document.querySelector(`[data-scroll-to="${selectedDateKey}"]`);
        scrollElement?.scrollIntoView({behavior: 'smooth', block: 'start'});

        hasScrolledToPosition = true;
      }, 800);

      return () => {
        clearTimeout(to);
      };
    },
    [redirectToToday, selectedDateKey],
  );

  if (redirectToToday) return <Navigate to={`/calendar/${todayKey}`} />;

  const selectedLabel = formatDate(parseISO(selectedDateKey), 'EEEE d MMMM', appLanguage);

  if (isCalendarDataLoading && !features.TimeslotsUI) {
    return (
      <>
        <Heading>
          <CalendarPageHeader title={showCalendar ? t('translation:Calendar') : selectedLabel} />
        </Heading>
        <Canvas centerContent>
          <Loader />
        </Canvas>
      </>
    );
  }

  function openFilterModal() {
    addPages([
      <NewFilterCard
        filterSlice={'CalendarOverview_Connections'}
        searchBarPlaceholderText={t('connection:SearchConnectionPlaceholder')}
        searchByProperties={['email', 'name']}
      />,
    ]);
    openModal();
  }

  return (
    <>
      <Heading>
        {showCalendar ? (
          <CalendarPageHeader className="gap-4 justify-between items-center flex">
            <H1>{t('translation:Calendar')}</H1>
            <IconButton
              icon="filter"
              iconButton="tertiary"
              onClick={openFilterModal}
            />
          </CalendarPageHeader>
        ) : (
          <CalendarPageHeader className="gap-4 justify-center items-center grid grid-cols-[1fr_4fr_1fr]">
            <IconButton
              className=" aligself-start justify-self-start bg-transparent shadow-none"
              icon={'caretLeft'}
              iconButton="tertiary"
              onClick={() => setShowCalendar(true)}
            />
            <H4 className="text-center">{selectedLabel}</H4>
          </CalendarPageHeader>
        )}

        <FlexRow
          alignItems="center"
          justifyContent="center"
          sm={{display: 'none'}}>
          <H4 data-testid="pages-Calendar-Calendar_calendarHeader">ksk</H4>
        </FlexRow>
      </Heading>

      <CalendarLayout>
        <StyledCalendarDayOverview
          $showCalendar={showCalendar}
          className={features.TimeslotsUI ? 'bg-white' : ''}>
          <CalendarHorizontalWeekOverviewWrapper>
            <CalendarHorizontalWeekOverview calendarWeeks={calendarWeeks} />
          </CalendarHorizontalWeekOverviewWrapper>

          <OverviewWrapper className={features.TimeslotsUI ? 'h-full lg:!py-0 shadow-none' : ''}>
            <FlexRow
              alignItems="center"
              justifyContent={features.TimeslotsUI ? 'flex-start' : 'center'}
              sm={!features.TimeslotsUI ? {display: 'none'} : undefined}>
              <div className="flex items-center justify-between gap-5 w-full min-h-10">
                <H1
                  data-testid="pages-Calendar-Calendar_calendarHeader"
                  className={cn({
                    'max-sm:hidden': features.TimeslotsUI,
                  })}>
                  {selectedLabel}
                </H1>

                {features.TimeslotsUI && !isBeforeToday(selectedDateKey) && !isCalendarDataLoading && (
                  <WorkdayActions selectedDate={selectedDateKey} />
                )}
              </div>
            </FlexRow>

            {features.TimeslotsUI ? (
              <DaySummary
                selectedDate={selectedDateKey}
                isLoading={isCalendarDataLoading}
              />
            ) : (
              <CalendarDayOverview date={selectedDateKey} />
            )}
          </OverviewWrapper>
        </StyledCalendarDayOverview>

        <CalendarWeekOverview $showCalendar={showCalendar}>
          <CalendarOverviewConnectionFilter />

          {calendarWeeks.map((data) => (
            <CalendarWeek
              key={`calendarCard-${data.weekNumber}`}
              weekNumber={data.weekNumber}
              weekType={data.weekType}>
              {data.data.map((day) => (
                <CalendarWeekDay
                  date={day.date}
                  key={day.date}
                  isToday={day.isToday}
                  toggleDayAndWeekView={toggleDayAndWeekView}
                  showConnectionsWithIds={selectedCalendarConnectionFilterIds}
                  isLoading={isCalendarDataLoading}
                />
              ))}
            </CalendarWeek>
          ))}
        </CalendarWeekOverview>
      </CalendarLayout>
    </>
  );
};
